/* eslint-disable @typescript-eslint/no-unused-vars */
import { useEffect, ReactNode, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import dayjs from 'dayjs';

// react-router-dom components
import { useLocation } from 'react-router-dom';

// Material Dashboard 2 PRO React TS components
import {
  // DefaultNavbar,
  FaIcon,
  MDBox,
  Footer,
  NavBar,
  Sidenav
} from '@components/index';

import { Link } from '@mui/material';

// Material Dashboard 2 PRO React context
import {
  useMaterialUIController,
  setLayout,
  setMiniSidenav,
  setUserModifiedSidenav
} from '@context/MDContext';

import routes from '@constants/navItems';
import brandLogo from '@assets/images/DS_Logo.png';

import FeedBackModal from '@components/FeedbackModal';
import { setUserNotifications } from '@components/Notification/slice';

import { Grid } from '@mui/material';
import { sendAmplitudeData } from '@utils/amplitude';
import { getPageName } from '@utils/common';
import {
  PAGE_VISITED,
  VIEW_PROJECT_BOARD,
  VISITED_PROJECT_BOARD
} from '@constants/eventNames';
import { sendGAData } from '@utils/googleAnalytics';
import { resetChartConfigData } from '@pages/EditChart/slice';
import { useCreateFeedBackMutation } from '@api/projects';

import { MSG_EVENT, MSG_TYPE, NotificationData } from '@DStypes/notifications';
import { RootState } from '@store/store';
import PlanUpgradeModal from '@components/PlanUpgradeModal';
import usePricing from '@hooks/usePricing';
import { resetPlanErrors } from '@store/AppSlice';
import PlanBanner from '@components/PlanBanner';
import RoutesPath from '@constants/RoutesPath';
import { getBannerSessionValidity } from '@utils/plans';
import reactStringReplace from 'react-string-replace';
import { useUpdateNotificationAsReadMutation } from '@api/notifications';
import useToast from '@hooks/useToast';

interface MainLayoutProps {
  children: ReactNode;
  pageTitle: string;
  sectionTitle?: string;
  createdAt?: any;
  showBackButton?: boolean;
  sectionTooltip?: string;
  titleTooltip?: string;
  goBackMsg?: string;
  goBackDestination?: string;
  padding?: string | number;
  page?: string;
  isPreview?: boolean;
  toggleConfigurator?: (type: number) => void;
  projectCount?: number;
  showUploadLimitMessage?: boolean;
}

function MainLayout({
  children,
  pageTitle,
  sectionTooltip = '',
  titleTooltip = '',
  sectionTitle = 'PROJECTS',
  goBackMsg = '',
  createdAt,
  showBackButton = false,
  goBackDestination,
  padding,
  page,
  isPreview = false,
  // toggleConfigurator,
  showUploadLimitMessage
}: MainLayoutProps): JSX.Element {
  const [controller, dispatch] = useMaterialUIController();
  const { miniSidenav, sidenavColor, userModifiedSidenav } = controller;
  const { pathname } = useLocation();
  const storeDispatch = useDispatch();
  const { planUpdateError } = usePricing();

  const userDetails = useSelector((state: RootState) => state?.userDetails);

  const planValidityText = useSelector(
    (state: RootState) => state?.AppData.planValidityText
  );
  const subscriptionFlag = useSelector(
    (state: RootState) => state?.AppData?.subKeyFlag
  );
  const currentProjectData = useSelector(
    (state: RootState) => state?.projectBoard?.currentProjectData
  );

  const notifications = useSelector(
    (state: RootState) => state?.notificationsData
  );

  const [onMouseEnter, setOnMouseEnter] = useState(false);
  const [showPlanUpgradeBanner, setShowPlanUpgradeBanner] = useState(false);
  const [showPlanUpgradeModal, setShowPlanUpgradeModal] = useState(false);
  const [isFeedBackModalOpen, setIsFeedBackModalOpen] = useState(false);
  const { pushSuccessToast } = useToast();

  const [createFeedBack, { isSuccess }] = useCreateFeedBackMutation();
  const [markAsRead] = useUpdateNotificationAsReadMutation();

  useEffect(() => {
    setLayout(dispatch, 'dashboard');
  }, [pathname]);

  // Open sidenav when mouse enter on mini sidenav
  const handleOnMouseEnter = () => {
    // setMiniSidenav(dispatch, false);
    setOnMouseEnter(true);
  };

  // Close sidenav when mouse leave mini sidenav
  const handleOnMouseLeave = () => {
    if (onMouseEnter)
      // setMiniSidenav(dispatch, true);
      setOnMouseEnter(false);
  };

  useEffect(() => {
    if (planUpdateError && subscriptionFlag) setShowPlanUpgradeModal(true);
    else setShowPlanUpgradeModal(false);
  }, [planUpdateError, subscriptionFlag]);

  useEffect(() => {
    if (planValidityText.value && subscriptionFlag)
      setShowPlanUpgradeBanner(true);
    else setShowPlanUpgradeBanner(false);
  }, [planValidityText.value, subscriptionFlag]);

  useEffect(() => {
    const excludedPages = [
      'Project Board',
      'Deleted Projects',
      'Project Review',
      'Edit chart',
      'My Projects'
    ];

    if (userDetails?.user?.id) {
      const pageName = getPageName();

      let eventProperties: any = {};

      if (
        pageName === 'Project Board' &&
        currentProjectData?.project_datafiles?.[0]?.filetype
      ) {
        eventProperties = {
          'Chart Count': pageTitle.split(' ')[0],
          file_type: currentProjectData?.project_datafiles[0]?.filetype
        };
        sendAmplitudeData(VISITED_PROJECT_BOARD, eventProperties);
        storeDispatch(resetChartConfigData());

        return;
      } else {
        eventProperties = {};
      }

      if (!excludedPages.includes(pageName)) {
        sendAmplitudeData(PAGE_VISITED, eventProperties);
        sendGAData(PAGE_VISITED, eventProperties);
      }
      if (pageName === 'Project Board Preview')
        sendAmplitudeData(VIEW_PROJECT_BOARD);

      if (pageName !== 'Edit chart') storeDispatch(resetChartConfigData());
    }
  }, [
    userDetails?.user?.id,
    currentProjectData?.project_datafiles?.[0]?.filetype
  ]);

  const handleArrowClick = () => {
    setMiniSidenav(dispatch, !miniSidenav);
    if (!userModifiedSidenav) setUserModifiedSidenav(dispatch, true);
  };

  const onSubmitFeedBack = (payload: any) => {
    createFeedBack(payload);
  };

  useEffect(() => {
    if (window.innerWidth <= 1200) setMiniSidenav(dispatch, true);
  }, [window.innerWidth]);

  useEffect(() => {
    if (isSuccess) {
      setIsFeedBackModalOpen(false);

      const payload: NotificationData = {
        created_at: dayjs(new Date())
          .utc()
          .format('YYYY-MM-DDTHH:mm:ss.SSS[000][Z]'),
        id: dayjs(new Date()).utc().format('YYYY-MM-DDTHH:mm:ss.SSS[000][Z]'),
        is_read: false,
        is_silent: false,
        is_user_message: true,
        message_event: MSG_EVENT.FEEDBACK_SUBMITTED,
        message_text:
          'Thank you! Your support request is successfully submitted. Someone from our team will get back to you within 24-48 hours.',
        updated_at: dayjs(new Date())
          .utc()
          .format('YYYY-MM-DDTHH:mm:ss.SSS[000][Z]'),
        message_type: MSG_TYPE.SUCCESS,
        message_data: JSON.stringify({ project: { id: '', name: '' } }),
        linkKeyword: 'here.',
        replaceKeyword: '<click here>',
        is_important: false,
        recipient_obj: 0,
        isAnchorElement: true
      };

      storeDispatch(setUserNotifications(payload));
    }
  }, [isSuccess]);

  const isBannerOpen =
    window.location.href.includes(RoutesPath.MY_PROJECTS) &&
    subscriptionFlag &&
    showPlanUpgradeBanner &&
    getBannerSessionValidity();

  useEffect(() => {
    const handlePlanUpdateNotification = async () => {
      const messages: NotificationData[] = [];

      if (notifications.unreadMessages?.length) {
        notifications.unreadMessages.forEach((notification) => {
          if (notification.message_event === MSG_EVENT.PLAN_UPDATED)
            messages.push(notification);
        });

        if (messages && messages?.[0]) {
          const messageData = messages[0].message_text;
          const htmlMsg = reactStringReplace(messageData, '\n', () => <br />);

          await markAsRead({
            id: userDetails.user.id as string,
            message_id: [messages?.[0]?.id as string]
          });
          pushSuccessToast(<>{htmlMsg}</>);
        }
      }
    };

    handlePlanUpdateNotification();
  }, [notifications.unreadMessages]);

  return (
    <>
      <Grid
        container
        onClick={handleArrowClick}
        sx={({ palette }) => ({
          '&:hover': {
            opacity: 1,
            border: `2px solid ${palette.secondary.main}`
          },
          opacity: onMouseEnter ? 0.7 : 0,
          justifyContent: 'center',
          alignContent: 'center',
          position: 'fixed',
          left: miniSidenav ? '92px' : '219px',
          top: '53px',
          width: '22px',
          height: '22px',
          // boxShadow: lg,
          border: `2px solid ${palette.info.main}`,
          borderRadius: '50%',
          zIndex: 10,
          cursor: 'pointer',
          transition: 'ease 0.3s',
          background: palette.white.main
        })}
      >
        <FaIcon
          typographyProps={{ variant: 'xs', color: 'info' }}
          icon={['fas', miniSidenav ? 'angle-right' : 'angle-left']}
        />
      </Grid>
      {isPreview ? (
        <>
        <MDBox
        component={Link}
        href='https://datasquirrel.ai'
        target='_blank'
        sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', marginTop: '16px', marginLeft: '16px', marginBottom: '0px' }}
        >
          <MDBox
              component='img'
              src={brandLogo}
              alt='DataSquirrel.ai Brand'
              width={{ xs: '1.6rem', sm: '2.5rem' }}
              sx={{ marginBottom: '0px' }}
          />
        </MDBox>
        <Grid container spacing={8} direction='column' sx={{ marginTop: '0px', paddingTop: '0px' }}>
          {/* <Grid item>
            <DefaultNavbar
              routes={[]}
              brand={brandLogo}
              imageBrand={true}
              subtitle='Powered by DataSquirrel! '
              action={{
                type: 'external',
                route: 'https://www.datasquirrel.ai/',
                label: 'Sign Up!',
                color: 'light',
                textColor: 'info',
                textTransform: 'uppercase',
                variant: 'contained'
              }}
              toggleConfigurator={toggleConfigurator}
              showBeta={true}
            />
          </Grid> */}
          <Grid item sx={{ pt: '0px !important' }}>
            <MDBox
              bgColor='white'
              sx={() => ({
                p: padding || 3,
                pb: 0,
                position: 'relative',
                minHeight: '90vh',
                overflowX: 'hidden'
              })}
            >
              {children}
            </MDBox>
          </Grid>
          <Footer socials={false} />
        </Grid>
        </>
      ) : (
        <>
          {isBannerOpen && (
            <PlanBanner
              planValidityText={planValidityText}
              setShowPlanUpgradeBanner={setShowPlanUpgradeBanner}
            />
          )}
          <MDBox
            bgColor='white'
            sx={({ breakpoints, transitions, functions: { pxToRem } }) => ({
              p: padding || 3,
              paddingTop: '7px !important',
              pb: subscriptionFlag && showPlanUpgradeBanner ? 0 : '20px',
              position: 'relative',
              minHeight: '80vh',
              overflowX: 'hidden',
              [breakpoints.up('xl')]: {
                marginLeft: miniSidenav ? pxToRem(108) : pxToRem(230),
                transition: transitions.create(
                  ['margin-left', 'margin-right'],
                  {
                    easing: transitions.easing.easeInOut,
                    duration: transitions.duration.standard
                  }
                )
              }
            })}
          >
            <div
              style={{
                minHeight: `calc(100vh - ${isBannerOpen ? '78px' : '48px'})`,
                maxHeight: isBannerOpen ? `calc(100vh - 78px)` : 'unset'
              }}
            >
              <NavBar
                pageTitle={pageTitle}
                sectionTitle={sectionTitle}
                createdAt={createdAt}
                showBackButton={showBackButton}
                sectionTooltip={sectionTooltip}
                titleTooltip={titleTooltip}
                goBackMsg={goBackMsg}
                goBackDestination={goBackDestination}
                page={page}
                showUploadLimitMessage={showUploadLimitMessage}
              />
              {children}
            </div>
            <Sidenav
              color={sidenavColor}
              plan={userDetails?.plan}
              isTrial={userDetails?.is_trial}
              routes={routes}
              onMouseEnter={handleOnMouseEnter}
              onMouseLeave={handleOnMouseLeave}
              isBannerOpen={isBannerOpen}
            />
            {/* {showSquirrelBot && (
              <Tooltip title=''>
                <MDBox
                  sx={{
                    position: 'fixed',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bottom: 50,
                    right: 20,
                    background: 'white',
                    borderRadius: 50,
                    boxShadow: '0 3px 10px rgb(0 0 0 / 0.2)',
                    cursor: 'pointer',
                    zIndex: 50
                  }}
                  onClick={() => {
                    setIsFeedBackModalOpen(true);
                  }}
                >
                  <MDBox
                    component='img'
                    src={brandLogo}
                    alt='Brand'
                    sx={{
                      height: 50,
                      width: 50,
                      padding: '11px'
                    }}
                  />
                </MDBox>
              </Tooltip>
            )} */}
          </MDBox>
          {isFeedBackModalOpen && (
            <FeedBackModal
              open={isFeedBackModalOpen}
              onClose={() => setIsFeedBackModalOpen(false)}
              onSubmitFeedBack={onSubmitFeedBack}
            />
          )}
        </>
      )}
      {subscriptionFlag && showPlanUpgradeModal && (
        <PlanUpgradeModal
          open={subscriptionFlag && showPlanUpgradeModal}
          planError={planUpdateError}
          onClose={() => storeDispatch(resetPlanErrors())}
        />
      )}
    </>
  );
}

export default MainLayout;
